import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import React, { Fragment, useEffect, useState } from "react";
import { getFeatureAccess } from "../api/userAccess";
import { logout } from "../util/authentication";
import { Grid, Divider, AppBar, Toolbar } from "@mui/material";
import { useMediaQuery } from "react-responsive";
import {
  DESKTOP_MIN_WIDTH_PIXELS,
  APPBAR_HEIGHT_PIXELS,
  SPECIAL_USER_DASHBOARD_TITLE,
  FEATURE_ACCESS_TYPE,
  SPECIAL_ACCESS_FEATURES,
} from "../util/constants";
import EnvironmentChip from "../components/EnvironmentChip";
import PageLoading from "../components/PageLoading";
import PropTypes from "prop-types";
import HealthCheck from "../components/specialAccess/HeathCheck";
import GenerateReportsDialog from "../components/specialAccess/GenerateReportsDialog";
import EmployeeDataLookupDialog from "../components/specialAccess/EmployeeDataLookupDialog";
import ServiceVersionDialog from "../components/specialAccess/ServiceVersionDialog";
import FeatureAccessControlDialog from "../components/specialAccess/FeatureAccessControlDialog";
import ProxyLoginDialog from "../components/specialAccess/ProxyLoginDialog";
import SpecialAccessQuickLink from "../components/specialAccess/SpecialAccessQuickLink";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
  },
  appBar: {
    height: `${APPBAR_HEIGHT_PIXELS}px`,
  },
  toolbarMargin: theme.mixins.toolbar,
  starsLogo: {
    [theme.breakpoints.down("md")]: {
      fontSize: "15px",
    },
    marginLeft: "-75px",
    fontSize: "35px",
    cursor: "pointer",
  },
  chip: {
    [theme.breakpoints.down("md")]: {
      marginTop: "18px",
      marginLeft: "-65px",
    },
    marginLeft: "5px",
  },
  middle: {
    height: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pageTitle: {
    [theme.breakpoints.down("md")]: {
      margin: "auto",
      marginTop: "20px",
      marginLeft: "15px",
      fontSize: "20px",
    },
    fontSize: "32px",
    marginTop: "10px",
    marginLeft: "75px",
    textAlign: "left",
    color: theme.palette.primary.swaBlue,
  },
  accessGroupTitle: {
    [theme.breakpoints.down("md")]: {
      margin: "auto",
      marginLeft: "20px",
      fontSize: "18px",
    },
    marginTop: "30px",
    fontSize: "20px",
    marginLeft: "85px",
    textAlign: "left",
    color: theme.palette.primary.swaGray2,
  },
  quickLinkButtonGroup: {
    [theme.breakpoints.down("md")]: {
      margin: "auto",
    },
    display: "flex",
    justifyContent: "left",
    marginLeft: "75px",
    width: "100%",
    flexWrap: "wrap",
    color: theme.palette.secondary.swaWhite,
  },
  logOutButton: {
    [theme.breakpoints.up("xl")]: {
      marginRight: "-10em",
    },
    [theme.breakpoints.down("md")]: {
      height: "36px",
      minWidth: "95vw",
      margin: "auto",
      marginTop: "20px",
      marginBottom: "5px",
    },
    width: "200px",
    height: "50px",
    margin: "auto",
    marginTop: ".75em",
  },
}));

const SpecialUserHomePage = (props) => {
  const classes = useStyles();
  const isDesktopView = useMediaQuery({ minWidth: DESKTOP_MIN_WIDTH_PIXELS });
  const [specialUserFeatures, setSpecialUserFeatures] = useState({});
  const [isSpecialFeaturesLoading, setIsSpecialFeaturesLoading] = useState(true);

  const [isProxyLoginDialogOpen, setIsProxyLoginDialogOpen] = useState(false);
  const [isFeatureAccessDialogOpen, setIsFeatureAccessDialogOpen] = useState(false);

  const [isServiceVersionDialogOpen, setIsServiceVersionDialogOpen] = useState(false);

  const [isGenerateReportsDialogOpen, setIsGenerateReportsDialogOpen] = useState(false);
  const [isEmployeeDataLookupDialogOpen, setIsEmployeeDataLookupDialogOpen] = useState(false);

  useEffect(() => {
    fetchSpecialUserFeatures();
  }, []);

  const fetchSpecialUserFeatures = () => {
    getFeatureAccess(FEATURE_ACCESS_TYPE.SPECIAL)
      .then((res) => res.json())
      .then((payload) => {
        setIsSpecialFeaturesLoading(false);
        setSpecialUserFeatures(payload.features);
      });
  };

  const getReports = () => {
    if (
      SPECIAL_ACCESS_FEATURES.GENERATE_REPORTS in specialUserFeatures &&
      "reports" in specialUserFeatures[SPECIAL_ACCESS_FEATURES.GENERATE_REPORTS]
    ) {
      return specialUserFeatures[SPECIAL_ACCESS_FEATURES.GENERATE_REPORTS].reports;
    } else {
      return [];
    }
  };

  const handleProxyLogInButtonClick = () => {
    setIsProxyLoginDialogOpen(true);
  };

  const handleFeatureAccessButtonClick = () => {
    setIsFeatureAccessDialogOpen(true);
  };

  const handleServiceVersionButtonClick = () => {
    setIsServiceVersionDialogOpen(true);
  };

  const handleGenerateReportsButtonClick = () => {
    setIsGenerateReportsDialogOpen(true);
  };

  const handleEmployeeDataLookupButtonClick = () => {
    setIsEmployeeDataLookupDialogOpen(true);
  };

  const renderPageHeader = () => {
    return (
      <div>
        <Grid item xs={12}>
          <Typography component={"div"}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={classes.pageTitle}>{SPECIAL_USER_DASHBOARD_TITLE}</Box>
              </Grid>
              {isDesktopView && (
                <Grid item xs={3}>
                  <Button
                    className={classes.logOutButton}
                    variant="contained"
                    color="error"
                    size="large"
                    onClick={logMeOut}
                  >
                    LOG OUT
                  </Button>
                </Grid>
              )}
            </Grid>
          </Typography>
        </Grid>

        <Divider className="divider" />
      </div>
    );
  };

  const renderAccessGroupHeader = (title) => {
    return (
      <div>
        <Grid container>
          <Grid item xs={12}>
            <Typography component={"div"}>
              <Grid container spacing={3}>
                <Grid item xs={10}>
                  <Box className={classes.accessGroupTitle}>{title}</Box>
                </Grid>
              </Grid>
            </Typography>
          </Grid>
        </Grid>
        <Divider className="divider" />
      </div>
    );
  };

  const renderTools = () => {
    let features = [];
    for (const feature in specialUserFeatures) {
      features.push(specialUserFeatures[feature]);
    }

    const quickLinksMenuList = (features) => {
      let quickLinks = [];
      const userMenuItems = features;
      if (isDesktopView) {
        userMenuItems.forEach((menuItem) => {
          if (menuItem.isHomeButton) {
            quickLinks.push(menuItem);
          }
        });
      } else {
        userMenuItems.forEach((menuItem) => {
          if (menuItem.isActiveOnMobile) {
            if (menuItem.isHomeButton) {
              quickLinks.push(menuItem);
            }
          }
        });
      }
      quickLinks.sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0));
      return quickLinks;
    };

    const getOnClickForFeature = (feature) => {
      switch (feature.key) {
        case SPECIAL_ACCESS_FEATURES.PROXY_LOGIN:
          return handleProxyLogInButtonClick;
        case SPECIAL_ACCESS_FEATURES.FEATURE_ACCESS:
          return handleFeatureAccessButtonClick;
        case SPECIAL_ACCESS_FEATURES.GENERATE_REPORTS:
          return handleGenerateReportsButtonClick;
        case SPECIAL_ACCESS_FEATURES.EMPLOYEE_DATA_LOOKUP:
          return handleEmployeeDataLookupButtonClick;
        case SPECIAL_ACCESS_FEATURES.SERVICE_VERSION:
          return handleServiceVersionButtonClick;
        default:
          return () => {};
      }
    };

    const renderQuickLinks = () => {
      return quickLinksMenuList(features).map((item, idx) => {
        const handleOnClick = getOnClickForFeature(item);
        return (
          <SpecialAccessQuickLink idx={idx} description={item.description} onClick={handleOnClick} label={item.label} />
        );
      });
    };

    return (
      <Grid container>
        <Grid item xs={12} className={classes.quickLinkButtonGroup}>
          {renderQuickLinks()}
        </Grid>
      </Grid>
    );
  };

  const renderSpecialUserOptions = () => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12}>
              {renderAccessGroupHeader("Special User Tools")}
            </Grid>
            <Grid item xs={12}>
              {renderTools()}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const logMeOut = async () => {
    await logout();
  };

  const MyToolbar = ({ classes }) => (
    <Fragment>
      <AppBar>
        <Toolbar>
          <Grid container className={classes.appBar}>
            <Grid item xs={12} className={classes.middle}>
              <h1 className={classes.starsLogo}>STARS</h1>
              <div className={classes.chip}>
                <EnvironmentChip />
              </div>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <div className={classes.toolbarMargin} />
    </Fragment>
  );

  return (
    <>
      {isSpecialFeaturesLoading ? (
        <PageLoading />
      ) : (
        <>
          <MyToolbar classes={classes} />
          {renderPageHeader()}
          <HealthCheck />
          {renderSpecialUserOptions()}

          <Grid container>
            <Grid item xs={12} className={classes.quickLinkButtonGroup}>
              {!isDesktopView && (
                <Button
                  className={classes.logOutButton}
                  variant="contained"
                  color="error"
                  size="large"
                  onClick={logMeOut}
                >
                  LOG OUT
                </Button>
              )}
            </Grid>
          </Grid>
        </>
      )}
      {isProxyLoginDialogOpen && (
        <ProxyLoginDialog
          isDialogOpen={isProxyLoginDialogOpen}
          setIsDialogOpen={setIsProxyLoginDialogOpen}
          setCookieSessionActiveFunc={props.setCookieSessionActiveFunc}
          isDesktopView={isDesktopView}
        />
      )}

      {isFeatureAccessDialogOpen && (
        <FeatureAccessControlDialog
          isDialogOpen={isFeatureAccessDialogOpen}
          setIsDialogOpen={setIsFeatureAccessDialogOpen}
          isDesktopView={isDesktopView}
        />
      )}

      {isServiceVersionDialogOpen && (
        <ServiceVersionDialog
          isDialogOpen={isServiceVersionDialogOpen}
          setIsDialogOpen={setIsServiceVersionDialogOpen}
          setCookieSessionActiveFunc={props.setCookieSessionActiveFunc}
          isDesktopView={isDesktopView}
        />
      )}

      {isGenerateReportsDialogOpen && (
        <GenerateReportsDialog
          isDialogOpen={isGenerateReportsDialogOpen}
          setIsDialogOpen={setIsGenerateReportsDialogOpen}
          setCookieSessionActiveFunc={props.setCookieSessionActiveFunc}
          isDesktopView={isDesktopView}
          reports={getReports()}
        />
      )}

      {isEmployeeDataLookupDialogOpen && (
        <EmployeeDataLookupDialog
          isDialogOpen={isEmployeeDataLookupDialogOpen}
          setIsDialogOpen={setIsEmployeeDataLookupDialogOpen}
          isDesktopView={isDesktopView}
        />
      )}
    </>
  );
};

SpecialUserHomePage.propTypes = {
  setCookieSessionActiveFunc: PropTypes.func.isRequired,
};

export default SpecialUserHomePage;
